import { useEffect, useState } from "react";
import { api, API, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Play, Save, FileSpreadsheet, Plus, Trash2, Share2 } from "lucide-react";

const MODULES = ["accounts", "contacts", "leads", "opportunities", "quotes", "complaints", "activities", "samples", "artworks", "contracts", "locations"];
const OPS = ["eq", "ne", "contains", "gt", "gte", "lt", "lte"];
const DEFAULT_COLS = {
  accounts: ["name", "account_number", "segment", "abc_class", "status", "annual_spend", "health_score", "owner_name"],
  contacts: ["first_name", "last_name", "title", "email", "buying_role"],
  leads: ["company", "contact_name", "source", "status", "score", "estimated_volume"],
  opportunities: ["name", "account_name", "stage", "amount", "probability", "product_family", "sourcing", "close_date", "owner_name"],
  quotes: ["quote_number", "account_name", "status", "total", "product_family", "sourcing", "version", "validity_date"],
  complaints: ["complaint_number", "account_name", "defect_category", "severity", "status", "claimed_value"],
  activities: ["type", "subject", "date", "status", "account_name", "owner_name"],
  samples: ["sample_number", "account_name", "product_type", "status", "due_date"],
  artworks: ["artwork_number", "name", "account_name", "status", "version"],
  contracts: ["contract_number", "name", "account_name", "end_date", "annual_value", "computed_status"],
  locations: ["name", "account_name", "site_type", "city", "country", "region"],
};

// Columns that store a picklist option id → resolve to the localized label for display.
const COL_PICKLIST = {
  defect_category: "defect_categories",
  severity: "severities",
  stage: "opp_stages",
};

export default function Reports() {
  const { t, canWrite, plLabel } = useApp();
  const [saved, setSaved] = useState([]);
  const [module, setModule] = useState("opportunities");
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const [filters, setFilters] = useState([]);
  const [cols, setCols] = useState(DEFAULT_COLS.opportunities);
  const [rows, setRows] = useState(null);
  const [currentId, setCurrentId] = useState(null);

  const loadSaved = () => api.get("/reports").then((r) => setSaved(r.data)).catch(() => {});
  useEffect(() => { loadSaved(); }, []);

  const pickModule = (m) => { setModule(m); setCols(DEFAULT_COLS[m] || []); setFilters([]); setRows(null); setCurrentId(null); };

  const run = async () => {
    try {
      const r = await api.post("/reports/run", { module, filters, columns: cols });
      setRows(r.data.rows);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const save = async () => {
    const body = { name: name || `${t(module)} Report`, module, filters, columns: cols, shared };
    try {
      if (currentId) await api.put(`/reports/${currentId}`, body);
      else { const r = await api.post("/reports", body); setCurrentId(r.data.id); }
      toast.success(t("save") + " ✓");
      loadSaved();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const openSaved = (rep) => {
    setModule(rep.module); setName(rep.name); setFilters(rep.filters || []);
    setCols(rep.columns || DEFAULT_COLS[rep.module] || []); setShared(!!rep.shared); setCurrentId(rep.id); setRows(null);
  };

  const exportXlsx = async () => {
    try {
      const token = localStorage.getItem("lf_token");
      const res = await fetch(`${API}/reports/export`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ module, filters, columns: cols, name: name || module }),
      });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${name || module}.xlsx`;
      a.click();
    } catch { toast.error("Export error"); }
  };

  const allFields = Object.keys(DEFAULT_COLS).includes(module)
    ? [...new Set([...DEFAULT_COLS[module], ...(rows?.[0] ? Object.keys(rows[0]) : [])])] : [];

  return (
    <div className="space-y-4" data-testid="reports-page">
      <h1 className="text-3xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "Work Sans" }}>{t("reports")}</h1>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
            <Input data-testid="report-name-input" placeholder={t("report_name")} value={name} onChange={(e) => setName(e.target.value)} />
            <Select value={module} onValueChange={pickModule}>
              <SelectTrigger data-testid="report-module-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MODULES.map((m) => <SelectItem key={m} value={m}>{t(m)}</SelectItem>)}</SelectContent>
            </Select>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 uppercase">{t("add_filter")}</span>
                <button data-testid="add-filter-btn" className="text-[#F5A623]" onClick={() => setFilters((f) => [...f, { field: allFields[0] || "name", op: "contains", value: "" }])}>
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              {filters.map((f, i) => (
                <div key={i} className="space-y-1 border border-gray-100 rounded-md p-2 relative" data-testid={`filter-row-${i}`}>
                  <button className="absolute top-1.5 right-1.5 text-gray-300 hover:text-red-500"
                    onClick={() => setFilters((fs) => fs.filter((_, j) => j !== i))}><Trash2 className="h-3 w-3" /></button>
                  <Select value={f.field} onValueChange={(v) => setFilters((fs) => fs.map((x, j) => j === i ? { ...x, field: v } : x))}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`filter-field-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{allFields.map((fl) => <SelectItem key={fl} value={fl}>{fl}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex gap-1">
                    <Select value={f.op} onValueChange={(v) => setFilters((fs) => fs.map((x, j) => j === i ? { ...x, op: v } : x))}>
                      <SelectTrigger className="h-8 text-xs w-28" data-testid={`filter-op-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{OPS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input className="h-8 text-xs" data-testid={`filter-value-${i}`} value={f.value}
                      onChange={(e) => setFilters((fs) => fs.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} />
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-1">
              <span className="text-xs font-semibold text-gray-500 uppercase">{t("columns")}</span>
              <div className="grid grid-cols-1 gap-1 max-h-40 overflow-y-auto border rounded-md p-2">
                {allFields.map((fl) => (
                  <label key={fl} className="flex items-center gap-2 text-xs cursor-pointer">
                    <Checkbox checked={cols.includes(fl)} onCheckedChange={(c) => setCols((cs) => c ? [...cs, fl] : cs.filter((x) => x !== fl))} />
                    {fl}
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox data-testid="report-shared-checkbox" checked={shared} onCheckedChange={(c) => setShared(!!c)} />
              <Share2 className="h-3.5 w-3.5 text-gray-400" />{t("shared")}
            </label>
            <div className="flex gap-2">
              <Button className="flex-1 bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={run} data-testid="run-report-btn">
                <Play className="h-3.5 w-3.5 mr-1" />{t("run_report")}
              </Button>
              {canWrite && (
                <Button variant="outline" onClick={save} data-testid="save-report-btn"><Save className="h-3.5 w-3.5" /></Button>
              )}
              <Button variant="outline" onClick={exportXlsx} data-testid="export-xlsx-btn"><FileSpreadsheet className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">{t("my_reports")}</p>
            {saved.length === 0 && <p className="text-xs text-gray-400">–</p>}
            {saved.map((rep) => (
              <button key={rep.id} data-testid="saved-report-item" onClick={() => openSaved(rep)}
                className={`block w-full text-left text-sm py-1.5 px-2 rounded hover:bg-amber-50 ${currentId === rep.id ? "bg-amber-50 font-medium" : ""}`}>
                {rep.name}
                <span className="text-[10px] text-gray-400 ml-1">{t(rep.module)}</span>
                {rep.shared && <Badge className="ml-1 bg-blue-50 text-blue-600 hover:bg-blue-50 text-[9px]">{t("shared")}</Badge>}
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-gray-200 rounded-lg overflow-hidden">
          {!rows && <p className="p-8 text-sm text-gray-400 text-center">{t("run_report")} →</p>}
          {rows && (
            <div className="overflow-x-auto">
              <p className="px-4 pt-3 text-xs text-gray-400">{rows.length} {t("records")}</p>
              <table className="w-full text-xs" data-testid="report-results-table">
                <thead>
                  <tr className="text-left uppercase tracking-wide text-gray-400 border-b">
                    {cols.map((c) => <th key={c} className="px-3 py-2">{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                      {cols.map((c) => {
                        const raw = r[c];
                        const pk = COL_PICKLIST[c];
                        const val = pk && raw != null && raw !== ""
                          ? plLabel(pk, raw)
                          : (typeof raw === "object" ? JSON.stringify(raw) : String(raw ?? "–"));
                        return <td key={c} className="px-3 py-1.5">{val}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
